import { metrics, logger } from './slardar-utils'

import { isBOE, isPPE, ttEnv } from './utils'

const refineUrl = (str: string, req?: any) => {
  let decodedUrl = ''
  try {
    decodedUrl = decodeURIComponent(str)
      .split('?')[0]
      .replace(/(^\d+$)/g, '')
  } catch (err) {
    if (req) {
      if (req.metrics && req.logger) {
        req.metrics.emitCounter(`decode.url.fail`, 1, undefined, {
          url: str,
        })
        req.logger.warn(`[decode url] ${str} fail. err=${(err as any).message}`)
      }
    } else {
      metrics.counter(`decode.url.fail`, {
        url: str,
      })
      logger.warn(`[decode url] ${str} fail. err=${(err as any).message}`)
    }
  }
  return decodedUrl
}

export interface IBaseResponse<T = string> {
  data?: T
  message: string
  prompts: string
  status: number
}

const decoratedFetch = (
  input: RequestInfo,
  init?: RequestInit & {
    retryMaxCount?: number
    timeout?: number
    rawRes?: boolean
    req?: {
      fetch: any
      metrics: any
      logger: any
    }
  }
): Promise<any> => {
  const { retryMaxCount = 1, timeout = 5000, req, rawRes = false } = init || {}
  let { headers = {} } = init || {}
  const url = typeof input === 'string' ? input : input.url
  const refinedUrl = refineUrl(url, req)
  const {
    fetch: bytedFetch,
    metrics: bytedMetrics,
    logger: bytedLogger,
  } = req || {}
  const inBrowser = typeof window !== 'undefined'
  if (!inBrowser) {
    // 开发与测试环境自动配置header
    console.log('[decorated-fetch:headers]:before', headers)
    if (headers) {
      // @ts-ignore
      headers.cookie = `${req?.headers?.cookie || ''}; ${headers.cookie || ''}`
    }

    // 透传浏览器插件设置的环境标签到服务端
    const envHeaders: Record<string, string> = {}
    const envReq: any = req
    if (envReq?.headers?.['x-tt-env']) {
      envHeaders['x-tt-env'] = envReq.headers['x-tt-env'] as string
    }
    if (envReq?.headers?.['x-use-ppe']) {
      envHeaders['x-use-ppe'] = envReq.headers['x-use-ppe'] as string
    } else if (envReq?.headers?.['x-use-boe']) {
      envHeaders['x-use-boe'] = envReq.headers['x-use-boe'] as string
    }
    headers = {
      ...headers,
      ...envHeaders,
    }

    // 根据 npm 脚本设置的环境标签
    if ((isBOE || isPPE) && ttEnv) {
      if (isBOE) {
        headers = {
          ...headers,
          'x-use-boe': '1',
          'x-tt-env': ttEnv,
        }
      }
      if (isPPE) {
        headers = {
          ...headers,
          'x-use-ppe': '1',
          'x-tt-env': ttEnv,
        }
      }
      console.log('[decorated-fetch:isBOE]:', isBOE)
      console.log('[decorated-fetch:isPPE]:', isPPE)
      console.log('[decorated-fetch:ttEnv]:', ttEnv)
      console.log('[decorated-fetch:headers]:after', headers)
    }
    return (typeof bytedFetch !== 'function' ? fetch : bytedFetch)(url, {
      ...init,
      handleMethod: refinedUrl || 'unknown_method',
      retry: retryMaxCount,
      timeout,
      headers,
    })
      .then((res: any) => {
        if (rawRes) {
          return res
        }
        if (!res.ok) {
          return res.text().then((text: string) => {
            throw new Error(text)
          })
        } else {
          return res.json()
        }
      })
      .catch((err: Error) => {
        console.error(err)
        if (bytedLogger) {
          bytedLogger.error(
            `[fetch fail] fetch ${url} error. error = ${err.message}`
          )
        }
        throw err
      })
  }

  let retryCount = 0
  const retry_able_fetch = (): Promise<Response> => {
    const controller = new AbortController()
    setTimeout(() => controller.abort(), timeout)
    const timeBeforeFetch = new Date().getTime()
    return fetch(url, {
      ...init,
      headers,
      signal: controller.signal,
    })
      .then((res) => {
        const timeAfterFetch = new Date().getTime()
        const duration = timeAfterFetch - timeBeforeFetch
        metrics.counter(`fetch.success`, {
          url: refinedUrl || url,
        })
        metrics.timer(`fetch.success`, duration, {
          url: refinedUrl || url,
        })
        return res
      })
      .then((res: any) => {
        if (rawRes) {
          return res
        }
        if (!res.ok) {
          return res.text().then((text: string) => {
            throw new Error(text)
          })
        } else {
          return res.json()
        }
      })
      .catch((err) => {
        if (err.name === 'AbortError') {
          metrics.counter(`fetch.timeout`, {
            url: refinedUrl || url,
          })
          logger.log(`[fetch timeout] fetch ${url} timeout`)
          if (retryCount < retryMaxCount) {
            retryCount += 1
            metrics.counter(`fetch.retry`, {
              url: refinedUrl || url,
            })
            logger.log(`[fetch retry] fetch ${url} retry.`)
            return retry_able_fetch()
          } else {
            metrics.counter(`fetch.fail`, {
              url: refinedUrl || url,
            })
            logger.error(`[fetch fail] fetch ${url} timeout. reach retry limit`)
          }
        } else {
          metrics.counter(`fetch.fail`, {
            url: refinedUrl || url,
          })
          logger.error(
            `[fetch fail] fetch ${url} error. error = ${err.message}`
          )
        }
        throw err
      })
  }

  return retry_able_fetch()
}

export default decoratedFetch
